import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import { useTranslation } from 'react-i18next'
import type { PlanPackage } from '@/services/billingService'
import { formatAmount } from './price'

type PurchaseRecordedDialogProps = {
  /** The package that was asked for; null keeps the dialog closed. */
  pkg: PlanPackage | null
  planName: string
  accountEmail: string
  onClose: () => void
}

/**
 * What the customer sees when the checkout answered `recorded`.
 *
 * No payment provider is connected yet, so this is deliberately not a receipt
 * and does not read like one: the request exists, nobody has been charged, and
 * a person grants the package by hand. Dressing that up as a completed
 * purchase would be the one lie this screen could tell.
 */
export default function PurchaseRecordedDialog({
  pkg,
  planName,
  accountEmail,
  onClose,
}: PurchaseRecordedDialogProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('plans')

  const price = pkg
    ? t(pkg.period === 'month' ? 'price.netPerMonth' : 'price.net', {
        amount: formatAmount(pkg.price_pln),
      })
    : ''

  return (
    <Dialog
      open={pkg !== null}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${theme.affexy.radii.md}px` } }}
    >
      <DialogTitle sx={{ fontFamily: theme.affexy.fonts.heading, fontSize: 20, fontWeight: 700, pb: 1 }}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <MarkEmailReadOutlinedIcon sx={{ fontSize: 22, color: c.coral }} />
          <span>{t('purchase.recordedTitle')}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {pkg && (
          <Box
            sx={{
              mb: 2,
              px: 1.75,
              py: 1.25,
              borderRadius: '12px',
              bgcolor: c.gray50,
              fontSize: 13.5,
              fontWeight: 700,
            }}
          >
            {t('packages.summary', { plan: planName, count: pkg.respondents, price })}
          </Box>
        )}
        <Typography sx={{ fontSize: 14, color: c.ink72 }}>{t('purchase.recordedBody')}</Typography>
        <Typography sx={{ mt: 1, fontSize: 13.5, color: c.ink56 }}>
          {t('purchase.recordedContact', { email: accountEmail })}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="contained" onClick={onClose} sx={{ fontWeight: 700 }}>
          {t('purchase.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
